import React, { useEffect, useState } from 'react';
import { remove, update } from '../services/session.service';
import { list } from '../services/event.service';
import { Link, useNavigate } from 'react-router-dom';
import DateRender from '../components/DateRender';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  AddSessionRequest,
  EventDetailResponse,
  SessionDetails
} from '../models/api';
import SessionDetailsForm from '../components/SessionDetailsForm';
import { useAuth } from '../contexts/AuthContext';
import { FaPlus } from 'react-icons/fa';
import { ReactComponent as CalendarTodayIcon } from '../assets/calendar_today_large.svg';
import { ReactComponent as LocationOnIcon } from '../assets/location_on.svg';
import ModalForm from '../components/Modal';
import CreateNewEvent from '../components/CreateNewEvent';
import Feedback from '../components/Feedback';
import Button from '../components/Button';
import EventContextMenu from '../components/EventContextMenu';
import { getUrlParams, removeUrlParam } from '../services/parameter.service';
import SessionSharingDetails from '../components/SessionSharingDetails';

const Sessions: React.FC = () => {
  const [sessions, setSessions] = useState([] as EventDetailResponse[]);
  const [errors, setErrors] = useState([] as string[]);
  const [loading, setLoading] = useState(false);
  const [loadMessage, setLoadMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addErrors, setAddErrors] = useState([] as string[]);
  const [deleteLoading, setDeleteLoading] = useState([] as string[]);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [openMenuIndexPast, setOpenMenuIndexPast] = useState<number | null>(
    null
  );

  const navigate = useNavigate();

  const { deletedSession } = getUrlParams();

  useEffect(() => {
    if (deletedSession) {
      setLoadMessage('The event has been deleted.');
      removeUrlParam('deletedSession');
    }
  }, []);

  const [editingSession, setEditingSession] = useState<SessionDetails | null>(
    null
  );
  const [sharingSession, setSharingSession] = useState<SessionDetails | null>(
    null
  );

  const toggleMenu = (index: number) => {
    setOpenMenuIndexPast(null);
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };
  const togglePastMenu = (index: number) => {
    setOpenMenuIndex(null);
    setOpenMenuIndexPast(openMenuIndexPast === index ? null : index);
  };

  const { userInfo } = useAuth();

  const getSessions = async () => {
    if (loading) {
      return;
    }
    setLoading(true);

    try {
      const result = await list();

      if (result.success && result.data) {
        setSessions(result.data.events || []);
      } else {
        setLoadMessage('');
        setErrors(result.errors);
      }
    } catch {
      setLoadMessage('');
      setErrors(['There was a problem loading the events']);
    }
    setLoading(false);
  };

  const handleDelete = async (session: EventDetailResponse) => {
    if (deleteLoading.includes(session.reference)) {
      return;
    }
    setDeleteLoading([...deleteLoading, session.reference]);

    const result = await remove(session.reference);

    if (result.success) {
      await getSessions();
      setLoadMessage('Session deleted');
    }

    setDeleteLoading(deleteLoading.filter((p) => p !== session.reference));
  };

  useEffect(() => {
    getSessions();

    // If getting 2 requests here on live, see https://stackoverflow.com/questions/72238175/why-useeffect-running-twice-and-how-to-handle-it-well-in-react
  }, []);

  const getPossessive = () => {
    const name = userInfo?.firstName ?? '';
    if (!name) {
      return 'Your';
    }

    if (name.slice(-1) === 's') {
      return `${name}'`;
    }

    return `${name}'s`;
  };

  const handleEdit = async (request: AddSessionRequest) => {
    if (!editingSession) {
      return false;
    }
    setAddLoading(true);

    try {
      const result = await update(editingSession.reference, request);
      if (result.success && result.data) {
        setLoadMessage('Updated');
        setEditingSession(null);
        await getSessions();
        return true;
      } else {
        setAddErrors(result.errors);
        return false;
      }
    } finally {
      setAddLoading(false);
    }
  };

  useEffect(() => {
    setOpenMenuIndex(null);
    setOpenMenuIndexPast(null);
  }, [isAdding, editingSession, sharingSession, deleteLoading]);

  return (
    <div className="card-page">
      <div className="header-container">
        <h1>{getPossessive()} Dashboard</h1>
      </div>

      {/* TODO: Remove this */}
      {isAdding && (
        <ModalForm fullScreen={true} onClose={() => setIsAdding(false)}>
          <CreateNewEvent />
        </ModalForm>
      )}

      {editingSession && (
        <ModalForm>
          <SessionDetailsForm
            errors={addErrors}
            isSubmitting={addLoading}
            model={editingSession}
            submit={handleEdit}
            close={() => {
              setEditingSession(null);
              setAddErrors([]);
            }}
            buttonText="Update"
          />
        </ModalForm>
      )}

      {sharingSession && (
        <SessionSharingDetails
          session={sharingSession}
          close={() => setSharingSession(null)}
        />
      )}

      <div className="page-intro">
        <p></p>
        <div className="actions">
          <Button onClick={() => navigate('/Sessions/New')}>
            <FaPlus />
            Create new event
          </Button>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && (
        <>
          {sessions.length === 0 && errors.length === 0 && (
            <>
              <p>You haven&apos;t created any events yet.</p>
              <p>
                Click <strong>&apos;Create new event&apos;</strong> to create a
                new one.
              </p>
            </>
          )}
          <Feedback message={loadMessage} type="success" />
          <Feedback messages={errors} type="error" />
          {sessions.length > 0 && (
            <div className="events-section">
              <h2>Upcoming events</h2>
              {sessions.filter((p) => !p.isPast).length > 0 && (
                <div className="card-container">
                  {sessions
                    .filter((p) => !p.isPast)
                    .map((p, index) => (
                      <div className="card" key={`${p.name} ${p.reference}`}>
                        <Link
                          to={`/Sessions/${p.reference}`}
                          className="card__body"
                        >
                          <div className="title">{p.name}</div>
                          <div className="details">
                            {p.startDateUtc && (
                              <div className="date icon-row">
                                <CalendarTodayIcon />
                                <DateRender dateString={p.startDateUtc} />
                              </div>
                            )}
                            {p.location && (
                              <div className="icon-row">
                                <LocationOnIcon />
                                {p.location}
                              </div>
                            )}
                          </div>
                          <div className="tags">
                            {p.totalShowings > 0 && (
                              <div className="tag">
                                {p.totalShowings} showing
                                {`${p.totalShowings === 1 ? '' : 's'}`}
                              </div>
                            )}
                            <div className="tag">
                              {p.totalPages} slide
                              {`${p.totalPages === 1 ? '' : 's'}`}
                            </div>
                            {p.sessionDetails.accessCode && (
                              <div className="tag code">
                                {p.sessionDetails.accessCode}
                              </div>
                            )}
                          </div>
                        </Link>

                        <EventContextMenu
                          deleteLoading={deleteLoading}
                          index={index}
                          navigateToPresent={(p) =>
                            navigate(
                              !p.hasMultipleCustomShowings && p.showingReference
                                ? `/Sessions/${p.reference}/Present/${p.showingReference}`
                                : !p.hasMultipleCustomShowings &&
                                    p.defaultShowingReference
                                  ? `/Sessions/${p.reference}/Present/${p.defaultShowingReference}`
                                  : `/Sessions/${p.reference}/Showings`
                            )
                          }
                          navigateToShowings={(p) =>
                            navigate(`/Sessions/${p.reference}/Showings`)
                          }
                          onDelete={(p) => handleDelete(p)}
                          onEdit={(p) => setEditingSession(p.sessionDetails)}
                          onShare={(p) => setSharingSession(p.sessionDetails)}
                          onToggle={(index) => toggleMenu(index)}
                          selectedIndex={openMenuIndex}
                          sessionDetails={p}
                        />
                      </div>
                    ))}
                </div>
              )}
              {sessions.filter((p) => !p.isPast).length === 0 && (
                <p>No upcoming events found</p>
              )}
            </div>
          )}
          {sessions.filter((p) => p.isPast).length > 0 && (
            <div className="events-section past">
              <h2>Past events</h2>
              <div className="card-container">
                {sessions
                  .filter((p) => p.isPast)
                  .map((p, index) => (
                    <div className="card" key={`${p.name} ${p.reference}`}>
                      <Link
                        to={`/Sessions/${p.reference}`}
                        className="card__body"
                      >
                        <div className="title">{p.name}</div>
                        <div className="details">
                          {p.startDateUtc && (
                            <div className="date icon-row">
                              <CalendarTodayIcon />
                              <DateRender dateString={p.startDateUtc} />
                            </div>
                          )}
                          {p.location && (
                            <div className="icon-row">
                              <LocationOnIcon />
                              {p.location}
                            </div>
                          )}
                        </div>
                        <div className="tags">
                          {p.totalShowings > 0 && (
                            <div className="tag">
                              {p.totalShowings} showing
                              {`${p.totalShowings === 1 ? '' : 's'}`}
                            </div>
                          )}
                          <div className="tag">
                            {p.totalPages} slide
                            {`${p.totalPages === 1 ? '' : 's'}`}
                          </div>
                          {p.sessionDetails.accessCode && (
                            <div className="tag code">
                              {p.sessionDetails.accessCode}
                            </div>
                          )}
                        </div>
                      </Link>

                      <EventContextMenu
                        deleteLoading={deleteLoading}
                        index={index}
                        navigateToPresent={(p) =>
                          navigate(
                            !p.hasMultipleCustomShowings && p.showingReference
                              ? `/Sessions/${p.reference}/Present/${p.showingReference}`
                              : !p.hasMultipleCustomShowings &&
                                  p.defaultShowingReference
                                ? `/Sessions/${p.reference}/Present/${p.defaultShowingReference}`
                                : `/Sessions/${p.reference}/Showings`
                          )
                        }
                        navigateToShowings={(p) =>
                          navigate(`/Sessions/${p.reference}/Showings`)
                        }
                        onDelete={(p) => handleDelete(p)}
                        onEdit={(p) => setEditingSession(p.sessionDetails)}
                        onShare={(p) => setSharingSession(p.sessionDetails)}
                        onToggle={(index) => togglePastMenu(index)}
                        selectedIndex={openMenuIndexPast}
                        sessionDetails={p}
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Sessions;
